import * as React from 'react';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Avatar from '@mui/material/Avatar';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Checkbox from '@mui/material/Checkbox';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import TextField from '@mui/material/TextField';
import noImage from './images/noImage.png';
import { FaPlusCircle, FaMinusCircle } from "react-icons/fa";
import Chip from '@mui/material/Chip';
import TranslateIcon from '@mui/icons-material/Translate';
import Button from '@mui/material/Button';
import Autocomplete from '@mui/material/Autocomplete';

import DropDown from './components/DropDown';
import KImage from './components/KImage';
import Config from './components/ClientConfig';
import Client from './components/Client';
import BackOfficeConfig from './Config';

import swal from 'sweetalert';

import Visibility from './Visibility';

let CAT_LANGS_CACHE = null;
let CAT_LANGS_BY_ID = null;
let CAT_LANGS_LOADING = null;
let SUBC_LANGS_CACHE = null;
let SUBC_LANGS_BY_ID = null;
let SUBC_LANGS_LOADING = null;

// Memorized image cell so images don't re-render on every keystroke/resize
const MemoKImage = React.memo(function MemoKImage({
  edit,
  src,
  image,       // <- fallback for src
  alt,
  imageText,   // <- fallback for alt
  saveBack,
  width = 128,
  height = 128,
}) {
  const srcStr = src !== undefined ? src : image;
  const altStr = alt !== undefined ? alt : imageText;
  const imgObj = { data: srcStr || noImage, width, height }; // build inside memo
  return (
    <KImage
      edit={edit}
      image={imgObj}
      imageText={altStr}
      saveBack={saveBack}
    />
  );
});
class CategoryTranslationsPanel extends React.Component {
  constructor(props){
    super(props);
    this.state = {
      translations: [],
      loading: false,
      isSavingTr: false,
      trEdit: {},                 // { [iref]: boolean } row-level edit toggles
      languages: [],
      langById: {},
      languagesReady: false,
    };
  }

  componentDidMount(){
    this.loadLanguages().then(()=> this.loadTranslations());
  }

  toRelative = (u) => (!u ? '' : String(u).replace(/^https?:\/\/[^/]+/i, ''));
  joinUrl = (base, path) => {
    const b = (base || '').endsWith('/') ? base : (base + '/');
    const p = (path || '').startsWith('/') ? path.slice(1) : path;
    return b + p;
  };

  async loadLanguages() {
    if (CAT_LANGS_CACHE && CAT_LANGS_BY_ID) {
      this.setState({ languages: CAT_LANGS_CACHE, langById: CAT_LANGS_BY_ID, languagesReady: true });
      return;
    }
    if (CAT_LANGS_LOADING) {
      await CAT_LANGS_LOADING;
      this.setState({ languages: CAT_LANGS_CACHE || [], langById: CAT_LANGS_BY_ID || {}, languagesReady: true });
      return;
    }
    CAT_LANGS_LOADING = (async () => {
      const base = this.toRelative(this.props.apiLanguages || Config.API_URL_LANGUAGE || '/api/1.0/languages/');
      const data = (this.props.client || new Client('none')).getData(base, {});
      const raw = Array.isArray(data?.Languages) ? data.Languages : [];
      const list = raw
        .map(x => ({ iref: String(x.iref), languageName: String(x.languageName || '').trim() }))
        .filter(x => x.iref && x.languageName);
      const map = {}; list.forEach(l => { map[l.iref] = l; });
      CAT_LANGS_CACHE = list; CAT_LANGS_BY_ID = map;
    })();
    try { await CAT_LANGS_LOADING; } finally { CAT_LANGS_LOADING = null; }
    this.setState({ languages: CAT_LANGS_CACHE, langById: CAT_LANGS_BY_ID, languagesReady: true });
  }

  async loadTranslations() {
    const categoryId = this.props.categoryId;
    if (!categoryId) return;
    this.setState({ loading: true });
    try {
      const base = this.toRelative(this.props.apiCategories || Config.API_URL_PRODUCT_CATEGORY || '/api/1.0/product_categories/');
      const url  = this.joinUrl(base, `${categoryId}/translations/`);
      const data = (this.props.client || new Client('none')).getData(url, {});
      const rows = Array.isArray(data?.translations) ? data.translations : [];
      const normalized = rows.map(r => {
        const langId = (r.language && typeof r.language === 'object')
          ? String(r.language.iref)
          : (r.language != null ? String(r.language) : null);
        return { ...r, language: langId };
      });
      this.setState({ translations: normalized, loading: false });
    } catch(e){
      this.setState({ loading: false });
      swal('Error', e.message || String(e), 'error');
    }
  }

  onTrFieldChange = (iref, field, value) => {
    this.setState(s => ({
      translations: (s.translations || []).map(r => String(r.iref) === String(iref) ? { ...r, [field]: value } : r)
    }));
  };

  hasOpenDraft = () => (this.state.translations || []).some(t => Number(t.iref) < 0);
  canEditTrRow = (t) => !!this.props.allowEdit && (this.state.trEdit?.[t.iref] || t.iref < 0);
  toggleTrEdit = (iref, force) => {
    this.setState(s => ({ trEdit: { ...s.trEdit, [iref]: typeof force === 'boolean' ? force : !s.trEdit?.[iref] } }));
  };

  addBlankTranslation = () => {
    if (!this.props.categoryId) { swal('Error','Save the category first, then add translations.','error'); return; }
    if (this.hasOpenDraft()) { swal('Heads up','Please finish or cancel the current draft first.','info'); return; }
    const tmpId = -Date.now();
    const newRow = { iref: tmpId, productCategory: this.props.categoryId, language: null, name: '', description: '', shortName: '' };
    this.setState(s => ({ translations: [newRow, ...(s.translations || [])], trEdit: { ...s.trEdit, [tmpId]: true } }));
  };

  saveTranslation = async (row) => {
    const categoryId = this.props.categoryId;
    if (!categoryId) { swal('Error','Save the category first, then add translations.','error'); return; }
    this.setState({ isSavingTr: true });

    // client duplicate guard
    if (row.language) {
      const dup = (this.state.translations || []).some(r =>
        String(r.language || '') === String(row.language) &&
        String(r.iref) !== String(row.iref)
      );
      if (dup) {
        this.setState({ isSavingTr: false });
        swal('Duplicate', 'A translation for this language already exists for this category.', 'warning');
        return;
      }
    }

    const isRealId = row.iref && Number(row.iref) > 0;
    const payload = {
      ...(isRealId ? { iref: row.iref } : {}),
      language_iref: row.language || null,
      name: (row.name ?? '').trim(),
      description: (row.description ?? '').trim(),
      shortName: (row.shortName ?? '').trim()
    };
    try {
      const base = this.toRelative(this.props.apiCategories || Config.API_URL_PRODUCT_CATEGORY || '/api/1.0/product_categories/');
      const url  = this.joinUrl(base, `${categoryId}/translations_save/`);
      const data = (this.props.client || new Client('none')).pushData(url, payload);
      if (data && data.status === 'OK') {
        const saved  = data.row;
        const langId = saved?.language && typeof saved.language === 'object'
          ? String(saved.language.iref) : String(saved.language || '');
        const merged = { ...saved, language: langId || null };
        this.setState(s => {
          const list = [...(s.translations || [])];
          const idx = list.findIndex(x => x.iref === row.iref || x.iref === merged.iref);
          if (idx >= 0) list[idx] = merged; else list.unshift(merged);
          return { translations: list, isSavingTr: false, trEdit: { ...s.trEdit, [merged.iref]: false } };
        });
        swal('Saved','Translation saved.','success',{ buttons:false, timer:900 });
      } else {
        const msg = (data && (data.message || JSON.stringify(data.errors))) || 'Unknown error';
        swal('Save failed', msg, 'error'); this.setState({ isSavingTr: false });
      }
    } catch (e) {
      swal('Save failed', e.message || String(e), 'error');
      this.setState({ isSavingTr: false });
    }
  };

  render(){
    const rows = this.state.translations || [];
    return (
      <Box sx={{ p: 1.0, display:'grid', gridTemplateColumns:'1fr', gap: 1 }}>
        <Box sx={{ display:'flex', gap: 1, alignItems:'center', justifyContent:'space-between' }}>
          <Typography variant="subtitle2">Translations</Typography>
          {this.props.allowEdit && this.props.categoryId && (
            <Button size="small" variant="outlined" onClick={this.addBlankTranslation} disabled={this.hasOpenDraft() || this.state.loading}>
              Add translation
            </Button>
          )}
        </Box>

        {this.state.loading ? (
          <Typography variant="body2" color="text.secondary">Loading…</Typography>
        ) : rows.length === 0 ? (
          <Typography variant="body2" color="text.secondary">No translations yet.</Typography>
        ) : (
          rows.map((t) => {
            const isTemp = (t.iref < 0);
            return (
              <Box key={t.iref} sx={{ p: 1, borderRadius: 1, border: '1px solid #e0e0e0', display:'grid', gridTemplateColumns:'200px 1fr', gap: 1 }}>
                {/* Language */}
                <Box sx={{ display:'grid', gap: .5 }}>
                  {this.canEditTrRow(t) ? (
                    <Autocomplete
                      options={this.state.languages}
                      size="small"
                      disablePortal
                      getOptionLabel={(opt) => opt?.languageName || ''}
                      isOptionEqualToValue={(opt, val) => opt?.iref === val?.iref}
                      value={(this.state.languagesReady && t.language) ? (this.state.langById[t.language] || null) : null}
                      onChange={(_, val) => this.onTrFieldChange(t.iref, 'language', val ? val.iref : null)}
                      renderInput={(params) => <TextField {...params} variant="outlined" label="Language" />}
                      ListboxProps={{ sx: { maxHeight: 200, p: 0 } }}
                      sx={{ minWidth: 200, mt: 1 }}
                      disabled={!this.state.languagesReady}
                    />
                  ) : (
                    <>
                      <Typography variant="caption" color="text.secondary">Language</Typography>
                      <Typography variant="body2">
                        {!this.state.languagesReady ? '…' : (t.language ? (this.state.langById[t.language]?.languageName || '—') : '—')}
                      </Typography>
                    </>
                  )}
                </Box>

                {/* Fields */}
                <Box sx={{ display:'grid', gap: 0.5 }}>
                  {this.canEditTrRow(t) ? (
                    <Box
                      sx={{
                        display:'grid',
                        columnGap: 0.5,
                        rowGap: 0.5,
                        gridTemplateColumns: '1fr 160px auto'
                      }}
                    >
                      {/* Name (col 1) */}
                      <TextField
                        size="small" margin="dense" fullWidth
                        label="Name" variant="outlined"
                        value={t.name || ''} inputProps={{ maxLength: 100 }}
                        onChange={(e)=>this.onTrFieldChange(t.iref, 'name', e.target.value)}
                        sx={{ gridColumn: '1 / span 1' }}
                      />
                      {/* Short name (col 2) */}
                      <TextField
                        size="small" margin="dense" fullWidth
                        label="Short name" variant="outlined"
                        value={t.shortName || ''} inputProps={{ maxLength: 16 }}
                        onChange={(e)=>this.onTrFieldChange(t.iref, 'shortName', e.target.value)}
                        sx={{ gridColumn: '2 / span 1' }}
                      />
                      {/* Actions (col 3) */}
                      <Box sx={{ gridColumn: '3 / span 1', display:'flex', gap: 1, justifyContent:'flex-end', alignItems:'center', mt: 1 }}>
                        <Button size="small" variant="contained" disabled={this.state.isSavingTr} onClick={()=>this.saveTranslation(t)}>
                          {this.state.isSavingTr ? 'Saving…' : (isTemp ? 'Add' : 'Save')}
                        </Button>
                        <Button
                          size="small"
                          onClick={()=>{
                            if (isTemp) {
                              this.setState(s => ({
                                translations: (s.translations || []).filter(x => x.iref !== t.iref),
                                trEdit: { ...s.trEdit, [t.iref]: false }
                              }));
                            } else {
                              this.toggleTrEdit(t.iref, false);
                            }
                          }}
                        >
                          Cancel
                        </Button>
                      </Box>
                      {/* Description (row 2, spans all) */}
                      <TextField
                        size="small" margin="dense" fullWidth
                        label="Description" variant="outlined"
                        multiline rows={2} inputProps={{ maxLength: 1024 }}
                        value={t.description || ''}
                        onChange={(e)=>this.onTrFieldChange(t.iref, 'description', e.target.value)}
                        sx={{ gridColumn: '1 / span 3' }}
                      />
                    </Box>
                  ) : (
                    <>
                      <Typography variant="subtitle2">{t.name || '—'}</Typography>
                      <Typography variant="body2" color="text.secondary">{t.description || '—'}</Typography>
                      <Typography variant="body2" color="text.secondary">Short: {t.shortName || '—'}</Typography>
                      {this.props.allowEdit && (
                        <Box sx={{ display:'flex', justifyContent:'flex-end', mt:.5 }}>
                          <Button size="small" onClick={()=>this.toggleTrEdit(t.iref, true)}>Edit</Button>
                        </Box>
                      )}
                    </>
                  )}
                </Box>
              </Box>
            );
          })
        )}
      </Box>
    );
  }
}

class SubcategoryTranslationsPanel extends React.Component {
  constructor(props){
    super(props);
    this.state = {
      translations: [],
      loading: false,
      isSavingTr: false,
      trEdit: {},                 // { [iref]: boolean }
      languages: [],
      langById: {},
      languagesReady: false,
    };
  }

  componentDidMount(){
    this.loadLanguages().then(()=> this.loadTranslations());
  }

  toRelative = (u) => (!u ? '' : String(u).replace(/^https?:\/\/[^/]+/i, ''));
  joinUrl = (base, path) => {
    const b = (base || '').endsWith('/') ? base : (base + '/');
    const p = (path || '').startsWith('/') ? path.slice(1) : path;
    return b + p;
  };

  async loadLanguages() {
    if (SUBC_LANGS_CACHE && SUBC_LANGS_BY_ID) {
      this.setState({ languages: SUBC_LANGS_CACHE, langById: SUBC_LANGS_BY_ID, languagesReady: true });
      return;
    }
    if (SUBC_LANGS_LOADING) {
      await SUBC_LANGS_LOADING;
      this.setState({ languages: SUBC_LANGS_CACHE || [], langById: SUBC_LANGS_BY_ID || {}, languagesReady: true });
      return;
    }
    SUBC_LANGS_LOADING = (async () => {
      const base = this.toRelative(this.props.apiLanguages || Config.API_URL_LANGUAGE || '/api/1.0/languages/');
      const data = (this.props.client || new Client('none')).getData(base, {});
      const raw = Array.isArray(data?.Languages) ? data.Languages : [];
      const list = raw
        .map(x => ({ iref: String(x.iref), languageName: String(x.languageName || '').trim() }))
        .filter(x => x.iref && x.languageName);
      const map = {}; list.forEach(l => { map[l.iref] = l; });
      SUBC_LANGS_CACHE = list; SUBC_LANGS_BY_ID = map;
    })();
    try { await SUBC_LANGS_LOADING; } finally { SUBC_LANGS_LOADING = null; }
    this.setState({ languages: SUBC_LANGS_CACHE, langById: SUBC_LANGS_BY_ID, languagesReady: true });
  }

  async loadTranslations() {
    const subId = this.props.subCategoryId;
    if (!subId) return;
    this.setState({ loading: true });
    try {
      const base = this.toRelative(this.props.apiSubCategories || Config.API_URL_PRODUCT_SUBCATEGORY || '/api/1.0/product_subcategories/');
      const url  = this.joinUrl(base, `${subId}/translations/`);
      const data = (this.props.client || new Client('none')).getData(url, {});
      const rows = Array.isArray(data?.translations) ? data.translations : [];
      const normalized = rows.map(r => {
        const langId = (r.language && typeof r.language === 'object')
          ? String(r.language.iref)
          : (r.language != null ? String(r.language) : null);
        return { ...r, language: langId };
      });
      this.setState({ translations: normalized, loading: false });
    } catch(e){
      this.setState({ loading: false });
      swal('Error', e.message || String(e), 'error');
    }
  }

  onTrFieldChange = (iref, field, value) => {
    this.setState(s => ({
      translations: (s.translations || []).map(r => String(r.iref) === String(iref) ? { ...r, [field]: value } : r)
    }));
  };

  hasOpenDraft = () => (this.state.translations || []).some(t => Number(t.iref) < 0);
  canEditTrRow = (t) => !!this.props.allowEdit && (this.state.trEdit?.[t.iref] || t.iref < 0);
  toggleTrEdit = (iref, force) => {
    this.setState(s => ({ trEdit: { ...s.trEdit, [iref]: typeof force === 'boolean' ? force : !s.trEdit?.[iref] } }));
  };

  addBlankTranslation = () => {
    if (!this.props.subCategoryId) { swal('Error','Save the subcategory first, then add translations.','error'); return; }
    if (this.hasOpenDraft()) { swal('Heads up','Please finish or cancel the current draft first.','info'); return; }
    const tmpId = -Date.now();
    const newRow = { iref: tmpId, productSubCategory: this.props.subCategoryId, language: null, name: '', description: '', shortName: '' };
    this.setState(s => ({ translations: [newRow, ...(s.translations || [])], trEdit: { ...s.trEdit, [tmpId]: true } }));
  };

  saveTranslation = async (row) => {
    const subId = this.props.subCategoryId;
    if (!subId) { swal('Error','Save the subcategory first, then add translations.','error'); return; }
    this.setState({ isSavingTr: true });

    // client duplicate guard
    if (row.language) {
      const dup = (this.state.translations || []).some(r =>
        String(r.language || '') === String(row.language) &&
        String(r.iref) !== String(row.iref)
      );
      if (dup) {
        this.setState({ isSavingTr: false });
        swal('Duplicate', 'A translation for this language already exists for this subcategory.', 'warning');
        return;
      }
    }

    const isRealId = row.iref && Number(row.iref) > 0;
    const payload = {
      ...(isRealId ? { iref: row.iref } : {}),
      language_iref: row.language || null,
      name: (row.name ?? '').trim(),
      description: (row.description ?? '').trim(),
      shortName: (row.shortName ?? '').trim()
    };
    try {
      const base = this.toRelative(this.props.apiSubCategories || Config.API_URL_PRODUCT_SUBCATEGORY || '/api/1.0/product_subcategories/');
      const url  = this.joinUrl(base, `${subId}/translations_save/`);
      const data = (this.props.client || new Client('none')).pushData(url, payload);
      if (data && data.status === 'OK') {
        const saved  = data.row;
        const langId = saved?.language && typeof saved.language === 'object'
          ? String(saved.language.iref) : String(saved.language || '');
        const merged = { ...saved, language: langId || null };
        this.setState(s => {
          const list = [...(s.translations || [])];
          const idx = list.findIndex(x => x.iref === row.iref || x.iref === merged.iref);
          if (idx >= 0) list[idx] = merged; else list.unshift(merged);
          return { translations: list, isSavingTr: false, trEdit: { ...s.trEdit, [merged.iref]: false } };
        });
        swal('Saved','Translation saved.','success',{ buttons:false, timer:900 });
      } else {
        const msg = (data && (data.message || JSON.stringify(data.errors))) || 'Unknown error';
        swal('Save failed', msg, 'error'); this.setState({ isSavingTr: false });
      }
    } catch (e) {
      swal('Save failed', e.message || String(e), 'error');
      this.setState({ isSavingTr: false });
    }
  };

  render(){
    const rows = this.state.translations || [];
    return (
      <Box sx={{ p: 1.0, display:'grid', gridTemplateColumns:'1fr', gap: 1 }}>
        <Box sx={{ display:'flex', gap: 1, alignItems:'center', justifyContent:'space-between' }}>
          <Typography variant="subtitle2">Translations</Typography>
          {this.props.allowEdit && this.props.subCategoryId && (
            <Button size="small" variant="outlined" onClick={this.addBlankTranslation} disabled={this.hasOpenDraft() || this.state.loading}>
              Add translation
            </Button>
          )}
        </Box>

        {this.state.loading ? (
          <Typography variant="body2" color="text.secondary">Loading…</Typography>
        ) : rows.length === 0 ? (
          <Typography variant="body2" color="text.secondary">No translations yet.</Typography>
        ) : (
          rows.map((t) => {
            const isTemp = (t.iref < 0);
            return (
              <Box key={t.iref} sx={{ p: 1, borderRadius: 1, border: '1px solid #e0e0e0', display:'grid', gridTemplateColumns:'200px 1fr', gap: 1 }}>
                {/* Language */}
                <Box sx={{ display:'grid', gap: .5 }}>
                  {this.canEditTrRow(t) ? (
                    <Autocomplete
                      options={this.state.languages}
                      size="small"
                      disablePortal
                      getOptionLabel={(opt) => opt?.languageName || ''}
                      isOptionEqualToValue={(opt, val) => opt?.iref === val?.iref}
                      value={(this.state.languagesReady && t.language) ? (this.state.langById[t.language] || null) : null}
                      onChange={(_, val) => this.onTrFieldChange(t.iref, 'language', val ? val.iref : null)}
                      renderInput={(params) => <TextField {...params} variant="outlined" label="Language" />}
                      ListboxProps={{ sx: { maxHeight: 200, p: 0 } }}
                      sx={{ minWidth: 200, mt: 1 }}
                      disabled={!this.state.languagesReady}
                    />
                  ) : (
                    <>
                      <Typography variant="caption" color="text.secondary">Language</Typography>
                      <Typography variant="body2">
                        {!this.state.languagesReady ? '…' : (t.language ? (this.state.langById[t.language]?.languageName || '—') : '—')}
                      </Typography>
                    </>
                  )}
                </Box>

                {/* Fields */}
                <Box sx={{ display:'grid', gap: 0.5 }}>
                  {this.canEditTrRow(t) ? (
                    <Box
                      sx={{
                        display:'grid',
                        columnGap: 0.5,
                        rowGap: 0.5,
                        gridTemplateColumns: '1fr 160px auto'
                      }}
                    >
                      {/* Name (col 1) */}
                      <TextField
                        size="small" margin="dense" fullWidth
                        label="Name" variant="outlined"
                        value={t.name || ''} inputProps={{ maxLength: 100 }}
                        onChange={(e)=>this.onTrFieldChange(t.iref, 'name', e.target.value)}
                        sx={{ gridColumn: '1 / span 1' }}
                      />
                      {/* Short name (col 2) */}
                      <TextField
                        size="small" margin="dense" fullWidth
                        label="Short name" variant="outlined"
                        value={t.shortName || ''} inputProps={{ maxLength: 16 }}
                        onChange={(e)=>this.onTrFieldChange(t.iref, 'shortName', e.target.value)}
                        sx={{ gridColumn: '2 / span 1' }}
                      />
                      {/* Actions (col 3) */}
                      <Box sx={{ gridColumn: '3 / span 1', display:'flex', gap: 1, justifyContent:'flex-end', alignItems:'center', mt: 1 }}>
                        <Button size="small" variant="contained" disabled={this.state.isSavingTr} onClick={()=>this.saveTranslation(t)}>
                          {this.state.isSavingTr ? 'Saving…' : (isTemp ? 'Add' : 'Save')}
                        </Button>
                        <Button
                          size="small"
                          onClick={()=>{
                            if (isTemp) {
                              this.setState(s => ({
                                translations: (s.translations || []).filter(x => x.iref !== t.iref),
                                trEdit: { ...s.trEdit, [t.iref]: false }
                              }));
                            } else {
                              this.toggleTrEdit(t.iref, false);
                            }
                          }}
                        >
                          Cancel
                        </Button>
                      </Box>
                      {/* Description (row 2, spans all) */}
                      <TextField
                        size="small" margin="dense" fullWidth
                        label="Description" variant="outlined"
                        multiline rows={2} inputProps={{ maxLength: 1024 }}
                        value={t.description || ''}
                        onChange={(e)=>this.onTrFieldChange(t.iref, 'description', e.target.value)}
                        sx={{ gridColumn: '1 / span 3' }}
                      />
                    </Box>
                  ) : (
                    <>
                      <Typography variant="subtitle2">{t.name || '—'}</Typography>
                      <Typography variant="body2" color="text.secondary">{t.description || '—'}</Typography>
                      <Typography variant="body2" color="text.secondary">Short: {t.shortName || '—'}</Typography>
                      {this.props.allowEdit && (
                        <Box sx={{ display:'flex', justifyContent:'flex-end', mt:.5 }}>
                          <Button size="small" onClick={()=>this.toggleTrEdit(t.iref, true)}>Edit</Button>
                        </Box>
                      )}
                    </>
                  )}
                </Box>
              </Box>
            );
          })
        )}
      </Box>
    );
  }
}

class SubcategoryRow extends React.Component {

  constructor(props) {
    super(props);

    this.state = {
      client: new Client ( "none" ),
      edit: false,
      row: {},
      category: "0",
      somethingHasChanged: false,
      nameValid: false,
      shortNameValid: false,
      priorityValid: false,
      descriptionValid: false,
      allowEdit: false
    };

    
    this.handleEditClick = this.handleEditClick.bind(this);
    this.getImage = this.getImage.bind(this);
    this.getImage_off = this.getImage_off.bind(this);    
    this.changeValue = this.changeValue.bind(this);
    this.pushData = this.pushData.bind(this);
  }

  handleEditClick ( ) {
    this.setState({ edit: !this.state.edit });
  }

  getImage ( newImage ) {
    if ( newImage ) {
      if ( this.state.row ) {
        let nRow = this.state.row;
        nRow.image = newImage;
        this.setState ( { row: nRow }, this.setState ( { somethingHasChanged: true } ) );
      }
    }
  }

  getImage_off ( newImage ) {
    if ( newImage ) {
      if ( this.state.row ) {
        let nRow = this.state.row;
        nRow.image_off = newImage;
        this.setState ( { row: nRow }, this.setState ( { somethingHasChanged: true } ) );
      }
    }
  }

  componentDidMount() {
    let row = {};
    if (this.props)
      if (this.props.allowEdit !== undefined)
        if ( this.props.allowEdit === true ) this.setState ({allowEdit: true});
    if ( this.props.row ) {
      row = this.props.row;
      this.setState ( { row: row } );
      if ( row.name.length > 0 ) this.setState ( {nameValid: true } );
      if ( row.shortName && row.shortName.length > 0 ) this.setState ( {shortNameValid: true } );
      if ( row.priority.toString().length > 0 ) this.setState ( {priorityValid: true } );
      if ( row.description.length > 0 ) this.setState ( {descriptionValid: true } );
    }
    if ( this.props.category ) this.setState ( { category: this.props.category } );
    if ( this.props.edit ) this.setState ( { edit: true } );
  }

  componentDidUpdate ( prevProps ) {
    if ( this.props !== prevProps ) {
      if (this.props)
        if (this.props.allowEdit !== undefined)
          if ( this.props.allowEdit === true ) this.setState ({allowEdit: true});
      if ( this.props.row ) {
        let row = this.props.row;
        this.setState ( { row: row } );
        if ( row.name.length > 0 ) this.setState ( {nameValid: true } );
        if ( row.shortName && row.shortName.length > 0 ) this.setState ( {shortNameValid: true } );
        if ( row.priority.toString().length > 0 ) this.setState ( {priorityValid: true } );
        if ( row.description.length > 0 ) this.setState ( {descriptionValid: true } );
      }
      if ( this.props.category ) this.setState ( { category: this.props.category } );
      if ( this.props.edit ) this.setState ( { edit: true } );
    }
  }

  changeValue ( e ) {
    let id = null;
    if ( e.target.id !== null ) id = e.target.id;
    let value = null;
    if  ( e.target.value !== null ) value = e.target.value;
    //if ( id !== null && value !== null && value !== undefined ) {
    if ( id ) {
      let row = {};
      if ( this.state.row ) row = this.state.row;
      switch ( id ) {
        case "name":
          if ( value.length > 0 ) {
            row.name = value;
            this.setState ( { nameValid: true });
          } else {
            row.name = "";
            this.setState ( { nameValid: false });
          }
          this.setState ( { somethingHasChanged: true } );
          break;
        case "shortName":
          if ( value.length > 0 ) {
            row.shortName = value;
            this.setState ( { shortNameValid: true });
          } else {
            row.shortName = "";
            this.setState ( { shortNameValid: false });
          }
          this.setState ( { somethingHasChanged: true } );
          break;
        case "description":
          if ( value.length > 0 ) {
            row.description = value;
            this.setState ( { descriptionValid: true });
          } else {
            row.description = "";
            this.setState ( { descriptionValid: false });
          }
          this.setState ( { somethingHasChanged: true } );
          break;
        case "priority":
          if ( value.length > 0 ) {
            row.priority = value;
            this.setState ( { priorityValid: true });
          } else {
            row.priority = 0;
            this.setState ( { priorityValid: false });
          }
          this.setState ( { somethingHasChanged: true } );
          break;
        case "isForSale":            
            row.isForSale = !row.isForSale;
            this.setState ( { somethingHasChanged: true } );
          break;
        default:
            console.warn(`Unhandled id: ${id}`);
            break;
      }
      this.setState ( { row: row });
    }
  }

  pushData ( e ) {
    if ( this.state.somethingHasChanged && this.state.nameValid && this.state.shortNameValid && this.state.priorityValid && this.state.descriptionValid ) {
      let qData = {
        category: this.state.category,
        data: this.state.row
      };
      let rdata = this.state.client.pushData ( Config.API_URL_PRODUCT_SUBCATEGORY_ADD, qData );
      if ( rdata ) {
        if ( rdata.status === "OK" ) {
          let uSubCategory = this.state.row;
          uSubCategory.iref = rdata.iref;
          this.setState ( { row: uSubCategory } );
          //this.props.subCategoryAdded ( );
          this.handleEditClick ( );
          swal ( "Success", "Successfully added/modified sub category ! (" + rdata.iref + ")", "success", {
            buttons: false,
            timer: 2000,
          })
          .then((value) => {
            //this.props.personUpdated ( );
            this.setState ( {edit: false} );
            this.setState ( { somethingHasChanged: false } );
            this.props.subCategoryAdded ( );
            //this.setState ( { addSubCategory: false } );
          });        
        } else {
          if ( rdata.message ) this.setState ( { errorMessage: rdata.message } );
          swal ( "Error", "Add/Update sub category failed !\n\n" + rdata.message, "error" );
        }
      }
    } else {
      swal ( "Error", "Add/Update sub category failed ! Some fields are not validated !", "error" );
    }
  }

  render() {
    let key = "";
    let iref = "";
    let name = "";
    let shortName = "";
    let img = noImage;
    let img_off = noImage;
    let description = "";
    let priority = 0;
    let isForSale = false;
    let productsCounter = 0;

    if ( this.state.row ) {
      let row = this.state.row;
      if ( row.sckey ) key = row.sckey;
      if ( row.iref ) iref = row.iref;
      if ( row.name ) name = row.name;
      if ( row.image ) img = row.image;
      if ( row.image_off ) img_off = row.image_off;
      if ( row.shortName ) shortName = row.shortName;
      if ( row.description ) description = row.description;
      if ( row.priority ) priority = row.priority;
      if ( row.isForSale ) isForSale = row.isForSale;
      if ( row.productsCounter ) productsCounter = row.productsCounter;
    }

    let imageName = name + "_on";
    let imageName_off = name + "_off";

    let showSave = false;
    if ( this.state.edit === true && this.state.somethingHasChanged === true && this.state.nameValid === true && this.state.shortNameValid === true && this.state.priorityValid === true && this.state.descriptionValid === true )
      showSave = true;

    return (
      <React.Fragment>
        <TableRow key={key}>
        <TableCell component="th" scope="row">
          {iref}
        </TableCell>
          <TableCell component="th" scope="row">
          {this.state.edit ?
            <TextField
              id="name"
              label="Name"
              variant="outlined"
              value={name || ''}
              onChange={this.changeValue}
              onFocus={event => { event.target.select(); }}
              inputProps={{maxLength:"100"}}
              error={this.state.nameValid === false}
              required={true}
              sx={{minWidth: "100px"}}
            />
            : name
          }
          </TableCell>
          <TableCell align="left">
          {this.state.edit ?
            <TextField
              id="shortName"
              label="Short Name"
              variant="outlined"
              value={shortName || ''}
              onChange={this.changeValue}
              onFocus={event => { event.target.select(); }}
              inputProps={{maxLength:"16"}}
              error={this.state.shortNameValid === false}
              required={true}
              sx={{minWidth: "100px"}}
            />
            : shortName
          }
          </TableCell>
          <TableCell align="left">
          {this.state.edit ?
            <TextField
              id="description"
              label="Description"
              variant="outlined"
              value={description || ''}
              onChange={this.changeValue}
              onFocus={event => { event.target.select(); }}
              inputProps={{maxLength:"255"}}
              error={this.state.descriptionValid === false}
              required={true}
              sx={{minWidth: "200px"}}
            />
            : description
          }
          </TableCell>
          <TableCell align="left">
          {this.state.edit ?
            <TextField
              id="priority"
              label="Priority"
              type="number"
              variant="outlined"
              value={priority || 0}
              onChange={this.changeValue}
              onFocus={event => { event.target.select(); }}
              inputProps={{min:0}}
              error={this.state.priorityValid === false}
              required={true}
              sx={{minWidth: "80px"}}
            />
            : priority
          }
          </TableCell>
          <TableCell align="left">
            {this.state.edit ?
              <Checkbox id="isForSale" color="success" checked={isForSale} onChange={this.changeValue}/>
              :
              <Checkbox  color="success" checked={isForSale} disabled/>
            }
          </TableCell>
          <TableCell>
            <MemoKImage
              edit = {this.state.edit}
              image = {img}
              imageText = {imageName}
              saveBack = {this.getImage}
           />
          </TableCell>
          <TableCell align="left">
            <MemoKImage
              edit = {this.state.edit}
              image = {img_off}
              imageText = {imageName_off}
              saveBack = {this.getImage_off}
           />
          </TableCell>

          <TableCell component="th" scope="row">
            {productsCounter}
          </TableCell>

          <TableCell align="left">
            <Chip
              icon={<TranslateIcon />}
              label="Translations"
              size="small"
              color={this.props.trOpen ? "primary" : "default"}
              variant={this.props.trOpen ? "filled" : "outlined"}
              onClick={() => this.props.toggleTr(this.state.row.iref)}
              sx={{ fontWeight: 600 }}
            />
          </TableCell>
          
          <TableCell align="right">
            <IconButton aria-label="settings" onClick={this.handleEditClick}>
                <EditIcon
                  sx={{ color: this.state.edit ? "#8f241d" : "#e3e3e5" }}                  
               />
            </IconButton>
            {showSave ?
              <IconButton aria-label="settings" onClick={this.pushData}>
                <SaveIcon
                  sx={{ color: "#4287f5" }}                
                />
              </IconButton>
              : ""}
          </TableCell>
        </TableRow>
      </React.Fragment>
      
    );

  }
}

  class Row extends React.Component {

    constructor(props) {
      super(props);
  
      this.state = {
        client: new Client ( "none" ),  
        categoriesWithSubCategories: null, 
        edit: false,   
        opened: false,
        row: {},
        category: "0",
        nameValid: false,
        shortNameValid: false,
        descriptionValid: false,
        priorityValid: false,
        image_off: noImage,
        somethingHasChanged: false,
        addSubCategory: false,
        trOpenBySubcategory: {},
        allowEdit: false
      };
  
      this.toggleAdd = this.toggleAdd.bind(this);
      this.handleEditClick = this.handleEditClick.bind(this);
      this.handleOpen = this.handleOpen.bind(this);
      this.getImage = this.getImage.bind(this);
      this.getImage_off = this.getImage_off.bind(this);
      this.changeValue = this.changeValue.bind(this);
      this.pushData = this.pushData.bind(this);
      this.changeOperation = this.changeOperation.bind(this);
      this.subCategoryAdded = this.subCategoryAdded.bind(this);
    }

    handleOpen ( ) {
      let opened = this.state.opened;
      this.setState ( { opened: !opened } );
    }

    handleEditClick ( ) {
      this.setState({ edit: !this.state.edit });
    }
    
    getImage ( newImage ) {
      if ( newImage ) {
        if ( this.state.row ) {
          let nRow = this.state.row;
          nRow.image = newImage;
          this.setState ( { row: nRow }, this.setState ( { somethingHasChanged: true } ) );
        }
      }
    }
  
    getImage_off ( newImage ) {
      if ( newImage ) {
        if ( this.state.row ) {
          let nRow = this.state.row;
          nRow.image_off = newImage;
          this.setState ( { row: nRow }, this.setState ( { somethingHasChanged: true } ) );
        }
      }
    }

    changeValue ( e ) {
      let id = null;
      if ( e.target.id !== null ) id = e.target.id;
      let value = null;
      if  ( e.target.value !== null ) value = e.target.value;
      //if ( id !== null && value !== null && value !== undefined ) {
      if ( id ) {
        let row = {};
        if ( this.state.row ) row = this.state.row;
        switch ( id ) {
          case "name":
            if ( value.length > 0 ) {
              row.name = value;
              this.setState ( { nameValid: true });
            } else {
              row.name = "";
              this.setState ( { nameValid: false });
            }
            this.setState ( { somethingHasChanged: true } );
            break;
          case "shortName":
            if ( value.length > 0 ) {
              row.shortName = value;
              this.setState ( { shortNameValid: true });
            } else {
              row.shortName = "";
              this.setState ( { shortNameValid: false });
            }
            this.setState ( { somethingHasChanged: true } );
            break;
          case "description":
            if ( value.length > 0 ) {
              row.description = value;
              this.setState ( { descriptionValid: true });
            } else {
              row.description = "";
              this.setState ( { descriptionValid: false });
            }
            this.setState ( { somethingHasChanged: true } );
            break;
          case "priority":
            if ( value.length > 0 ) {
              row.priority = value;
              this.setState ( { priorityValid: true });
            } else {
              row.priority = 0;
              this.setState ( { priorityValid: false });
            }
            this.setState ( { somethingHasChanged: true } );
            break;
          case "isForSale":            
            row.isForSale = !row.isForSale;
            this.setState ( { somethingHasChanged: true } );
            break;
          default:
            console.warn(`Unhandled id: ${id}`);
            break;
        }
        this.setState ( { row: row });
      }
    }

    componentDidMount() {
      let row = {};
      if (this.props)
        if (this.props.allowEdit !== undefined)
          if ( this.props.allowEdit === true ) this.setState ({allowEdit: true});
      if ( this.props.row ) {
        row = this.props.row;
        this.setState ( { row: row } );
        if ( row.name.length > 0 ) this.setState ( {nameValid: true } );
        if ( row.shortName && row.shortName.length > 0 ) this.setState ( {shortNameValid: true } );
        if ( row.priority.toString().length > 0 ) this.setState ( {priorityValid: true } );
        if ( row.description.length > 0 ) this.setState ( {descriptionValid: true } );
        
      }
      if ( this.props.category ) this.setState ( { category: this.props.category } );
      if ( this.props.edit ) this.setState ( { edit: true } );
    }

    componentDidUpdate ( prevProps ) {
      if ( this.props !== prevProps ) {
        if (this.props.allowEdit !== undefined)
          if ( this.props.allowEdit === true ) this.setState ({allowEdit: true});
        if ( this.props.row ) {
          let row = this.props.row;
          this.setState ( { row: row } );
          if ( row.name.length > 0 ) this.setState ( {nameValid: true } );
          if ( row.shortName && row.shortName.length > 0 ) this.setState ( {shortNameValid: true } );
          if ( row.priority.toString().length > 0 ) this.setState ( {priorityValid: true } );
          if ( row.description.length > 0 ) this.setState ( {descriptionValid: true } );
          if ( row.allowEdit === true ) this.setState ({allowEdit: true});
        }
        if ( this.props.category ) this.setState ( { category: this.props.category } );
        if ( this.props.edit ) this.setState ( { edit: true } );
      }
    }

    toggleAdd ( ) {
      if (!this.state.allowEdit)
        return;
      let addButton = this.state.addSubCategory;
      this.setState ( {addSubCategory: !addButton} );
      //this.props.toggleAdd ( !addButton );
    }

    toggleTrForSub = (subId, force) => {
      this.setState(s => ({
        trOpenBySubcategory: {
          ...s.trOpenBySubcategory,
          [subId]: typeof force === 'boolean' ? force : !s.trOpenBySubcategory?.[subId]
        }
      }));
    };

    pushData ( e ) {
      if ( this.state.somethingHasChanged && this.state.nameValid && this.state.shortNameValid && this.state.priorityValid && this.state.descriptionValid ) {
        let rdata = this.state.client.pushData ( Config.API_URL_PRODUCT_CATEGORY_ADD, this.state.row );
        if ( rdata ) {
          if ( rdata.status === "OK" ) {
            let uCategory = this.state.row;
            //uCategory.iref = rdata.iref;
            uCategory = rdata;
            this.setState ( { row: uCategory } );
            this.handleEditClick ( );
            swal ( "Success", "Successfully added/modified category ! (" + rdata.iref + ")", "success", {
              buttons: false,
              timer: 2000,
            })
            .then((value) => {
              //this.props.personUpdated ( );
              this.setState ( {edit: false} );
              this.setState ( { somethingHasChanged: false } );
              this.props.categoryAdded ();
            });        
          } else {
            if ( rdata.message ) this.setState ( { errorMessage: rdata.message } );
            swal ( "Error", "Add/Update category failed !\n\n" + rdata.message, "error" );
          }
        }
      } else {
        swal ( "Error", "Add/Update category failed ! Some fields are not validated !", "error" );
      }
    }

    changeOperation ( value ) {
      if ( value !== undefined ) {
        let uRow = this.state.row;
        uRow.operation = value;
        this.setState ( { row: uRow } );
        this.setState ( { somethingHasChanged: true } );
      }
    }

    subCategoryAdded ( ) {
      //this.props.getData ( );
      let queryData = {};
      queryData["orderBy_Name"] = true;
      let data = this.state.client.getData ( Config.API_URL_PRODUCT_CATEGORY + this.state.row.iref, queryData );
      this.setState ( { row: data });
      this.setState ( {addSubCategory: false } );
    }

    render() {
      let row = {};
      let subCategory = [];
      let catIref = "0";
      let image = noImage;
      let image_off = noImage;
      let isForSale = false;
      let operationType = 9;
      let operationType_text = "Undefined";
      let name = "";
      let shortName = "";
      let priority = 0;
      let description = "";
      let productsCounter = 0;

      if ( this.state.row ) {
        row = this.state.row;
        if ( row.iref ) catIref = row.iref;
        if ( row.name ) name = row.name;
        if ( row.shortName ) shortName = row.shortName;
        if ( row.priority ) priority = row.priority;
        if ( row.description ) description = row.description;
        if ( row.image ) image = row.image;
        if ( row.image_off ) image_off = row.image_off;
        if ( row.isForSale ) isForSale = row.isForSale;
        if ( row.subCategory ) subCategory = row.subCategory;
        if ( row.productsCounter ) productsCounter = row.productsCounter;
        if ( row.operation !== undefined ) {
          operationType = parseInt(row.operation);
          operationType_text = BackOfficeConfig.CATEGORY_OPERATION_TYPES[parseInt(operationType)];
        }
      }
      let imageName = name + "_on";
      let imageName_off = name + "_off";
      let showSave = false;
      if ( this.state.edit === true && this.state.somethingHasChanged === true && this.state.nameValid === true && this.state.shortNameValid === true && this.state.priorityValid === true && this.state.descriptionValid === true )
        showSave = true;

    return (
      <React.Fragment>
        <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>

          <TableCell>
            <IconButton
              aria-label="expand row"
              size="small"
              onClick={() => this.handleOpen()}
            >
              {this.state.opened ? <KeyboardArrowUpIcon/> : <KeyboardArrowDownIcon/>}
            </IconButton>
            {catIref}
          </TableCell>

          <TableCell component="th" scope="row">
            {this.state.edit ?
              <TextField
                id="name"
                label="Name"
                variant="outlined"
                value={name || ''}
                onChange={this.changeValue}
                onFocus={event => { event.target.select(); }}
                inputProps={{maxLength:"100"}}
                error={this.state.nameValid === false}
                required={true}
                sx={{minWidth: "100px"}}
             />
              : name
            }
          </TableCell>
          <TableCell align="left">
            {this.state.edit ?
              <TextField
                id="shortName"
                label="Short Name"
                variant="outlined"
                value={shortName || ''}
                onChange={this.changeValue}
                onFocus={event => { event.target.select(); }}
                inputProps={{maxLength:"16"}}
                error={this.state.shortNameValid === false}
                required={true}
                sx={{minWidth: "100px"}}
             />
              : shortName
            }
          </TableCell>
          <TableCell align="left">
            {this.state.edit ?
              <TextField
                id="description"
                label="Description"
                variant="outlined"
                value={description || ''}
                onChange={this.changeValue}
                onFocus={event => { event.target.select(); }}
                inputProps={{maxLength:"255"}}
                error={this.state.descriptionValid === false}
                required={true}
                sx={{minWidth: "200px"}}
             />
              : description
            }
          </TableCell>
          <TableCell align="left">
            {this.state.edit ?
              <TextField
                id="priority"
                label="Priority"
                type="number"
                variant="outlined"
                value={priority ?? 0}
                onChange={this.changeValue}
                onFocus={event => { event.target.select(); }}
                inputProps={{min:0}}
                error={this.state.priorityValid === false}
                required={true}
                sx={{minWidth: "80px"}}
             />
              : priority
            }
          </TableCell>

          <TableCell align="left">
            {this.state.edit ?
              <Checkbox id="isForSale" color="success" checked={isForSale} onChange={this.changeValue}/>
              :
              <Checkbox  color="success" checked={isForSale} disabled/>
            }

          </TableCell>

          <TableCell>
            {this.state.edit ?           
            <DropDown
              name="Operation"
              value={operationType}
              doptions={BackOfficeConfig.CATEGORY_OPERATION_TYPES}
              hasIcons={false} 
              minWidth={100}
              onChangeFunction={this.changeOperation}
            />
            : operationType_text }
          </TableCell>

          <TableCell align="left">
            <MemoKImage
              edit = {this.state.edit}
              image = {image}
              imageText = {imageName}
              saveBack = {this.getImage}
           />
          </TableCell>
          <TableCell align="left">
            <MemoKImage
              edit = {this.state.edit}
              image = {image_off}
              imageText = {imageName_off}
              saveBack = {this.getImage_off}
           />
          </TableCell>

          <TableCell align="left">
            {productsCounter}
          </TableCell> 

          <TableCell align="left">
            <Chip
              icon={<TranslateIcon />}
              label="Translations"
              size="small"
              color={this.props.trOpen ? "primary" : "default"}
              variant={this.props.trOpen ? "filled" : "outlined"}
              onClick={() => this.props.toggleTr(this.state.row.iref)}
              sx={{ fontWeight: 600 }}
            />
          </TableCell>

          <TableCell align="right">
            <IconButton aria-label="settings" onClick={this.handleEditClick}>
                <EditIcon
                  sx={{ color: this.state.edit ? "#8f241d" : "#e3e3e5" }}                  
               />
            </IconButton>
            {showSave ?
              <IconButton aria-label="settings" onClick={this.pushData}>
                <SaveIcon
                  sx={{ color: "#4287f5" }}                
                />
            </IconButton>
            : ""}
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={11}>
            <Collapse in={this.state.opened} timeout="auto" unmountOnExit>
              <Box sx={{ margin: 1 }}>
                <Typography variant="body1" gutterBottom component="div">
                  Subcategories
                </Typography>
                <Table size="small" aria-label="purchases" sx={{backgroundColor: "#eeeee4"}}>
                  <TableHead sx={{backgroundColor: "#ddddd4"}}>
                    <TableRow>
                      <TableCell align="left">
                        <IconButton aria-label="Add Location" title="Add Location" onClick={() => this.toggleAdd()}>       
                          <Avatar alt="Add Location" sx={{ width: 24, height: 24, color: "#273442" }}>
                            {this.state.addSubCategory ? <FaMinusCircle/> : <FaPlusCircle/>}
                          </Avatar>
                        </IconButton>
                      </TableCell>
                      <TableCell align="left">Name</TableCell>
                      <TableCell align="left">Short Name</TableCell>
                      <TableCell align="left">Description</TableCell>
                      <TableCell align="left">Priority</TableCell>
                      <TableCell align="left">Is For Sale</TableCell>
                      <TableCell align="left">Image (On)</TableCell>
                      <TableCell align="left">Image (Off)</TableCell>                      
                      <TableCell align="left">Products</TableCell>
                      <TableCell align="left">Translations</TableCell>
                      <TableCell align="right">Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {this.state.addSubCategory ?
                      <SubcategoryRow
                        key={0}
                        category={catIref}
                        edit={true}
                        subCategoryAdded={this.subCategoryAdded}
                        allowEdit={this.state.allowEdit}
                        toggleTr={this.toggleTrForSub}
                        trOpen={false}
                      />
                      : ""
                    }
                    {subCategory.map((scData) => (
                      <React.Fragment key={scData.iref}>
                        <SubcategoryRow
                          row={scData}
                          category={catIref}
                          subCategoryAdded={this.subCategoryAdded}
                          allowEdit={this.state.allowEdit}
                          toggleTr={this.toggleTrForSub}
                          trOpen={!!this.state.trOpenBySubcategory?.[scData.iref]}
                        />
                        <TableRow>
                          {/* inner table columns colSpan: now 10 (we added Translations column) */}
                          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={11}>
                            <Collapse in={!!this.state.trOpenBySubcategory?.[scData.iref]} timeout="auto" unmountOnExit>
                              <Box sx={{ background:'#fafafa', borderTop:'1px solid #eee', p:1.25 }}>
                                <SubcategoryTranslationsPanel
                                  subCategoryId={scData.iref}
                                  allowEdit={this.state.allowEdit}
                                  client={this.state.client}
                                  apiSubCategories={Config.API_URL_PRODUCT_SUBCATEGORY}
                                  apiLanguages={Config.API_URL_LANGUAGE}
                                />
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      </React.Fragment>
    );
  }
}

class Categories extends React.Component {

  constructor(props) {
    super(props);

    this.state = {
      client: new Client ( "none" ),  
      categoriesWithSubCategories: null,
      addCategory: false,
      trOpenByCategory: {},
    };

    this.getData = this.getData.bind(this);
    this.toggleAdd = this.toggleAdd.bind(this);
    this.categoryAdded = this.categoryAdded.bind(this);
    this.restricted = new Visibility ("products_categories");
  }

  toggleTrForCategory = (categoryId, force) => {
    this.setState(s => ({
      trOpenByCategory: {
        ...s.trOpenByCategory,
        [categoryId]: typeof force === 'boolean' ? force : !s.trOpenByCategory?.[categoryId]
      }
    }));
  };

  componentDidMount() {
    if (this.restricted.scope.visible)
      this.getData ( );
  }

  toggleAdd ( ) {
    if (!this.restricted.scope.edit)
      return;
    let addButton = this.state.addCategory;
    this.setState ( {addCategory: !addButton} );
    //this.props.toggleAdd ( !addButton );
  }

  getData ( ) {
    let queryData = {};
    queryData["orderBy_Name"] = true;
    let categoriesWithSubCategories = this.state.client.getData ( Config.API_URL_PRODUCT_CATEGORY_WITH_SUBCATEGORIES, queryData );
    this.setState ( { categoriesWithSubCategories: categoriesWithSubCategories.productCategories_withSubCategories });
  }

  categoryAdded ( ) {
    this.getData ( );
    this.setState ( { addCategory: false } );
  }

  render() {
    if (!this.restricted.scope.visible)
      return (<div></div>);

    let rows = [];
    if (this.state.categoriesWithSubCategories) {
      rows = this.state.categoriesWithSubCategories;
    }

    return (
      <TableContainer component={Paper} sx={{ maxHeight: 'calc(100vh - 80px)' }}>
      <Table stickyHeader aria-label="collapsible table">
        <TableHead sx={{'& .MuiTableCell-stickyHeader': { backgroundColor: '#273442', color: '#FFF' }}}>
          <TableRow>
            <TableCell>
              <IconButton aria-label="Add Category" title="Add Category" onClick={() => this.toggleAdd()}>       
                <Avatar alt="Add Category" sx={{ width: 24, height: 24, color: "#273442" }}>
                  {this.state.addCategory ? <FaMinusCircle/> : <FaPlusCircle/>}
                </Avatar>
              </IconButton>
            </TableCell>
            <TableCell sx={{backgroundColor: "#273442", color: "#FFFFFF !important"}}>Name</TableCell>
            <TableCell align="left" sx={{backgroundColor: "#273442", color: "#FFFFFF !important"}}>Short Name</TableCell>
            <TableCell align="left" sx={{backgroundColor: "#273442", color: "#FFFFFF !important"}}>Description</TableCell>
            <TableCell align="left" sx={{backgroundColor: "#273442", color: "#FFFFFF !important"}}>Priority</TableCell>
            <TableCell align="left" sx={{backgroundColor: "#273442", color: "#FFFFFF !important"}}>Is For Sale</TableCell>
            <TableCell align="left" sx={{backgroundColor: "#273442", color: "#FFFFFF !important"}}>Operation</TableCell>
            <TableCell align="left" sx={{backgroundColor: "#273442", color: "#FFFFFF !important"}}>Image (On)</TableCell>
            <TableCell align="left" sx={{backgroundColor: "#273442", color: "#FFFFFF !important"}}>Image (Off)</TableCell>
            <TableCell align="left" sx={{backgroundColor: "#273442", color: "#FFFFFF !important"}}>Products</TableCell>
            <TableCell align="left" sx={{backgroundColor: "#273442", color: "#FFFFFF !important"}}>Translations</TableCell>
            <TableCell align="right" sx={{backgroundColor: "#273442", color: "#FFFFFF !important"}}>Action</TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {this.state.addCategory && this.restricted.scope.edit ?
          <Row key={0} edit={true} categoryAdded={this.categoryAdded} allowEdit={this.restricted.scope.edit}/>
          : ""
          }
          {rows ? 
            rows.map((row) => (
              <React.Fragment key={row.iref}>
                <Row
                  row={row}
                  categoryAdded={this.categoryAdded}
                  allowEdit={this.restricted.scope.edit}
                  toggleTr={this.toggleTrForCategory}
                  trOpen={!!this.state.trOpenByCategory?.[row.iref]}
                />
                <TableRow>
                  {/* colSpan = total header columns (11 in your current head) */}
                  <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={12}>
                    <Collapse in={!!this.state.trOpenByCategory?.[row.iref]} timeout="auto" unmountOnExit>
                      <Box sx={{ background:'#fafafa', borderTop:'1px solid #eee', p:1.5 }}>
                        <CategoryTranslationsPanel
                          categoryId={row.iref}
                          allowEdit={this.restricted.scope.edit}
                          client={this.state.client}
                          apiCategories={Config.API_URL_PRODUCT_CATEGORY}
                          apiLanguages={Config.API_URL_LANGUAGE}
                        />
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))
            :
            ""
          }
        </TableBody>
      </Table>
    </TableContainer>
    );
  }
}

export default Categories;
